import { DragEndEvent } from "@dnd-kit/core";
import * as Parse from "parse";

import { makeAutoObservable } from "@opendash/state";

import type { ParseFilterState } from "../../../state/ParseFilterState";
import {
  ParseClassConfig,
  ParseClassSchema,
  ParseFieldSchema,
} from "../../../types";

import { $framework } from "@opendash/core";
import { translateSync } from "@opendash/i18n";
import { CanbanBoardType, CanbanViewType, DraggableData } from "..";
import { $parse } from "../../..";

export class CanbanState {
  public loading: boolean = true;
  public columnOrder: string[] = [];
  public cardOrder: string[] = [];

  private state: ParseFilterState;
  private renderCounter = 0;

  private view: CanbanViewType;
  private groupByField: string;
  private groupBySchema: ParseFieldSchema;
  private groupByPointer: string | null;
  private pointerSchema: ParseClassSchema | null;
  private pointerConfig: ParseClassConfig | null;
  private pointerSorted: boolean;
  private pointerData: Parse.Object[] = [];

  constructor(state: ParseFilterState) {
    makeAutoObservable(this);

    this.state = state;

    this.view = this.state.view as CanbanViewType;
    this.groupByField = this.view.groupByField;
    this.groupBySchema = this.state.schema.fields[this.groupByField];
    this.groupByPointer =
      this.groupBySchema.type === "Pointer"
        ? this.groupBySchema.targetClass
        : null;

    if (this.groupByPointer) {
      this.pointerConfig = $parse.ui.getClassConfig(this.groupByPointer);
      this.pointerSchema = $parse.ui.getClassSchema(this.groupByPointer);

      this.pointerSorted = !!this.pointerConfig?.orderField;

      this.fetchPointerData();
    } else {
      this.loading = false;
    }
  }

  rerender() {
    this.renderCounter += 1;
  }

  get columnsSortable() {
    return (
      this.pointerConfig?.orderField &&
      this.pointerSchema?.fields[this.pointerConfig.orderField]?.type ===
        "Number"
    );
  }

  get cardsSortable() {
    return (
      this.state.config.orderField &&
      this.state.schema.fields[this.state.config.orderField]?.type ===
        "Number" &&
      this.state.sortKey === this.state.config.orderField &&
      this.state.sortOrder === "ascend"
    );
  }

  get cardArray() {
    const result = this.state.data.result.slice();

    // if (this.cardsSortable) {
    //   result.sort(
    //     (a, b) =>
    //       a.get(this.state.config.orderField) -
    //       b.get(this.state.config.orderField)
    //   );
    // }

    return result;
  }

  get cardMap(): Record<string, Parse.Object> {
    return Object.fromEntries(this.cardArray.map((o) => [`card-${o.id}`, o]));
  }

  get columnsArray() {
    const array = Object.values(this.columnsMap);

    if (this.pointerSorted) {
      array.sort(
        (a, b) =>
          a.pointer?.get(this.pointerConfig.orderField) -
          b.pointer?.get(this.pointerConfig.orderField)
      );
    } else {
      array.sort((a, b) => a.key.localeCompare(b.key));
    }

    return array;
  }

  get columnsMap(): Record<string, CanbanBoardType> {
    const result: Record<string, CanbanBoardType> = {};

    const renderCounter = this.renderCounter;

    if (this.groupByPointer) {
      for (const pointer of this.pointerData) {
        const boardKey = `column-${pointer.id}`;

        result[boardKey] = {
          key: boardKey,
          renderCounter,
          value: pointer,
          pointer: pointer,
          pointerTitleFields: this.pointerConfig?.titleFields,
          label: boardKey,
          cards: [],
        };
      }

      for (const [key, object] of Object.entries(this.cardMap)) {
        const pointer: Parse.Object = object.get(this.groupByField);

        const boardKey = pointer ? `column-${pointer.id}` : "none";

        if (!result[boardKey]) {
          result[boardKey] = {
            key: boardKey,
            renderCounter,
            value: pointer,
            pointer: pointer,
            pointerTitleFields: this.pointerConfig?.titleFields,
            label: boardKey,
            cards: [],
          };
        }

        result[boardKey].cards.push({
          key,
          object,
        });
      }
    }

    if (this.cardsSortable) {
      for (const col of Object.values(result)) {
        col.cards.sort(
          (a, b) =>
            a.object.get(this.state.config.orderField) -
            b.object.get(this.state.config.orderField)
        );
      }
    }

    return result;
  }

  get columns(): CanbanBoardType[] {
    return Object.values(this.columnsMap);
  }

  getColumn(key: string) {
    return this.columnsMap[key];
  }

  getCard(key: string) {
    return this.cardMap[key];
  }

  private setPointerData(value: Parse.Object[]) {
    this.pointerData = value;
    this.loading = false;
  }

  private async fetchPointerData() {
    if (this.groupByPointer) {
      const query = new Parse.Query(this.groupByPointer);

      if (this.pointerConfig?.orderField) {
        query.ascending(this.pointerConfig.orderField);
      }

      const result = await query.find();

      this.setPointerData(result);
    }
  }

  private async reassignOrder(objects: Parse.Object[], field: string) {
    let i = 0;

    for (const object of objects) {
      object.set(field, i++);
    }

    this.rerender();

    await Parse.Object.saveAll(objects);
  }

  public onDragEnd(result: DragEndEvent) {
    const { active, over } = result;
    console.log(result);

    const view = this.state.view as CanbanViewType;
    const activeData = active.data.current as DraggableData;

    const source = activeData.origin;
    const destination = over.id;

    if (!destination || active.id === over.id) {
      return;
    }

    // A card is moved
    if (activeData.type === "CARD") {
      const card = this.getCard(result.active.id.toString());
      const column = this.getColumn(destination.toString());

      if (source !== destination) {
        // assign new column
        card.set(view.groupByField, column.value);

        if (!this.cardsSortable) {
          this.rerender();

          card.save().then(
            () => {
              $framework.antd_msg.success(
                translateSync("parse-admin:canban.reassign-card-group-success")
              );
            },
            (error) => {
              console.error(error);

              $framework.antd_msg.error(
                translateSync("parse-admin:canban.reassign-card-group-error")
              );
            }
          );
        }
      }

      if (this.cardsSortable) {
        const keys = column.cards.map((card) => card.key);
        //Find the index of source in column.cards
        const sourceIndex = keys.indexOf(source);
        //Find the index of destination in column.cards
        //TODO: Probably an error as destination is a columnid and not a cardid
        const destinationIndex = keys.indexOf(destination.toString());

        if (source === destination) {
          keys.splice(sourceIndex, 1);
        }

        keys.splice(destinationIndex, 0, source);

        const objects = keys.map((key) => this.getCard(key));

        this.reassignOrder(objects, this.state.config.orderField).then(
          () => {
            $framework.antd_msg.success(
              translateSync("parse-admin:canban.reassign-card-order-success")
            );
          },
          (error) => {
            console.error(error);

            $framework.antd_msg.error(
              translateSync("parse-admin:canban.reassign-card-order-error")
            );
          }
        );
      } else {
        if (source === destination) {
          $framework.antd_msg.info(
            translateSync("parse-admin:canban.reassign-card-order-disabled")
          );
        }
      }
    }

    // A column is moved
    if (activeData.type === "COLUMN") {
      if (!this.columnsSortable) {
        $framework.antd_msg.info(
          translateSync("parse-admin:canban.reassign-column-order-disabled")
        );
        return;
      }

      const keys = this.columnsArray.map((col) => col.key);

      // Find the index of source in columnsArray
      const sourceIndex = keys.indexOf(activeData.origin);
      // Find the index of destination in columnsArray
      const destinationIndex = keys.indexOf(destination.toString());

      keys.splice(sourceIndex, 1);
      keys.splice(destinationIndex, 0, activeData.origin);

      const objects = keys.map((key) => this.getColumn(key).pointer);

      this.reassignOrder(objects, this.pointerConfig.orderField).then(
        () => {
          $framework.antd_msg.success(
            translateSync("parse-admin:canban.reassign-column-order-success")
          );
        },
        (error) => {
          console.error(error);

          $framework.antd_msg.error(
            translateSync("parse-admin:canban.reassign-column-order-error")
          );
        }
      );
    }
  }
}
